import * as React from "react";
import { useSearchParams } from "react-router";

import type { ViewRecord } from "@/shared/domain/views";
import { useView } from "@/shared/domain/views/hooks";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";

import type { UseResourceViewOptions } from "../useResourceView";

import type { RoboqlFilters, RoboqlLink } from "./RoboqlLinkGate";
import { seedResourceViewOptions } from "./seedResourceViewOptions";
import type { LayoutDrafts } from "./useLayoutDrafts";

export interface ViewFromLink<TData> {
  /**
   * The View the table was built from, or `null` if it was built from
   * defaults. When set, the picker marks the View loaded straight away and
   * skips the usual fetch-then-apply step, which would otherwise re-apply a
   * View the table is already showing.
   */
  view: ViewRecord | null;
  /**
   * The options handed in, with the View merged in when there is one, or else
   * the filters of a `?query=` link.
   */
  options: UseResourceViewOptions<TData>;
  /**
   * Whether a `?query=` link decided the arrival, even one whose RoboQL failed
   * to parse. Memory and the default View stand down for it, as for `?view=`.
   */
  fromQueryLink: boolean;
}

/**
 * Start a resource table in the state a `?view=` link asks for.
 *
 * The route loader puts the linked View in the query cache before the page
 * renders, so `useView` has it on the first render, before the table sends
 * its first request. Decided once, at mount: a later change to `?view=` is
 * the customer picking from the panel, and the normal apply path handles it.
 *
 * `seedResourceViewOptions` decides whether the View can be restored without
 * the server. When it cannot, `view` is `null` and the options come back
 * untouched. `drafts` lets the seed include the customer's remembered tweaks
 * on the View, as applying it would.
 *
 * `roboqlLink`, what a `RoboqlLinkGate` around the list resolved, seeds the
 * filters instead when there is no View. The gate ignores `?query=` beside a
 * `?view=`, so at most one of the two seeds.
 */
export function useViewFromLink<TData>(
  options: UseResourceViewOptions<TData>,
  drafts?: LayoutDrafts,
  roboqlLink: RoboqlLink | null = null,
): ViewFromLink<TData> {
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get(VIEW_SEARCH_PARAM) ?? undefined;
  // The loader fetched this a moment ago. A refetch on mount would be the
  // second request seeding exists to avoid.
  const { data } = useView(linkedId, { staleTime: Infinity });

  const [view] = React.useState<ViewRecord | null>(() =>
    data !== undefined &&
    seedResourceViewOptions(data, options, drafts) !== null
      ? data
      : null,
  );

  let seeded: UseResourceViewOptions<TData> | null = null;
  if (view !== null) {
    seeded = seedResourceViewOptions(view, options, drafts);
  } else if (roboqlLink?.filters) {
    seeded = seedRoboqlFilters(roboqlLink.filters, options);
  }
  return {
    view,
    options: seeded ?? options,
    fromQueryLink: roboqlLink !== null,
  };
}

/**
 * Start the table on `filters`, with their `SORT BY` as the sort, the way
 * `applyFilters` mirrors one into a mounted table.
 */
function seedRoboqlFilters<TData>(
  filters: RoboqlFilters,
  options: UseResourceViewOptions<TData>,
): UseResourceViewOptions<TData> {
  const { sort_by: sortBy, sort_direction: sortDirection } = filters.spec;
  return {
    ...options,
    initialActiveFilters: filters,
    initialSort: sortBy
      ? {
          columnId: sortBy,
          direction: sortDirection === "DESC" ? "desc" : "asc",
        }
      : options.initialSort,
  };
}
